import { useState, useEffect, useMemo, useCallback } from "react";
import ReactApexChart from "react-apexcharts";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
    FiCalendar, FiCheckCircle, FiXCircle, FiClock, FiStar,
    FiTrendingUp, FiUser, FiScissors, FiBarChart2, FiPieChart,
    FiFilter, FiRefreshCw, FiChevronDown, FiChevronUp,
    FiX, FiAward, FiAlertCircle, FiActivity, FiArrowRight,
} from "react-icons/fi";
import { appointmentsAPI, servicesAPI, usersAPI, ratingsAPI } from "services/api";

// ============================================================
// SABITLER
// ============================================================
const MONTH_NAMES = [
    "Ocak","Şubat","Mart","Nisan","Mayıs","Haziran",
    "Temmuz","Ağustos","Eylül","Ekim","Kasım","Aralık",
];

const STATUS_CFG = {
    completed: { label: "Tamamlandı", color: "#10b981", bg: "rgba(16,185,129,0.12)" },
    cancelled:  { label: "İptal",      color: "#ef4444", bg: "rgba(239,68,68,0.12)"  },
    pending:    { label: "Bekleyen",   color: "#f59e0b", bg: "rgba(245,158,11,0.12)" },
    approved:   { label: "Onaylandı",  color: "#0ea5e9", bg: "rgba(14,165,233,0.12)" },
    no_show:    { label: "Gelmedi",    color: "#f97316", bg: "rgba(249,115,22,0.12)" },
};

const CHART_PALETTE = [
    "#4318FF","#10b981","#f59e0b","#ef4444","#0ea5e9",
    "#a855f7","#06b6d4","#f97316","#84cc16","#ec4899",
];

const formatCurrency = (v) =>
    Number(v || 0).toLocaleString("tr-TR", { style: "currency", currency: "TRY" });

const formatDate = (d) => {
    if (!d) return "—";
    return new Date(d).toLocaleDateString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric" });
};

const getInitials = (name = "", surname = "") =>
    ((name[0] || "") + (surname[0] || "")).toUpperCase() || "?";

// ============================================================
// SKELETON LOADERLAR
// ============================================================
const KpiSkeleton = () => (
    <div className="animate-pulse rounded-3xl h-36 bg-gray-200 dark:bg-navy-700"></div>
);

const ChartSkeleton = ({ height = 320 }) => (
    <div className="animate-pulse flex flex-col items-center justify-center bg-gray-50 dark:bg-navy-900/40 rounded-2xl gap-3" style={{ height }}>
        <div className="w-14 h-14 rounded-full bg-gray-200 dark:bg-navy-700"></div>
        <div className="h-3 w-28 bg-gray-200 dark:bg-navy-700 rounded-full"></div>
        <div className="h-2 w-20 bg-gray-200 dark:bg-navy-700 rounded-full"></div>
    </div>
);

const TableSkeleton = ({ rows = 5 }) => (
    <div className="space-y-2 p-4">
        {Array.from({ length: rows }).map((_, i) => (
            <div key={i} className="animate-pulse flex items-center gap-3 p-3 rounded-xl bg-gray-50 dark:bg-navy-900">
                <div className="w-8 h-8 rounded-lg bg-gray-200 dark:bg-navy-700 shrink-0"></div>
                <div className="flex-1 space-y-1.5">
                    <div className="h-3 bg-gray-200 dark:bg-navy-700 rounded-full w-3/5"></div>
                    <div className="h-2 bg-gray-200 dark:bg-navy-700 rounded-full w-2/5"></div>
                </div>
                <div className="h-5 w-16 bg-gray-200 dark:bg-navy-700 rounded-lg shrink-0"></div>
            </div>
        ))}
    </div>
);

// ============================================================
// KPI KARTI (Gradient)
// ============================================================
const KpiCard = ({ label, value, subValue, subLabel, icon, gradient }) => (
    <div
        className="relative overflow-hidden rounded-3xl p-5 text-white shadow-lg"
        style={{ background: gradient }}
    >
        <div className="absolute -right-4 -top-4 w-24 h-24 rounded-full bg-white/10"></div>
        <div className="absolute right-2 bottom-2 w-12 h-12 rounded-full bg-white/5"></div>
        <div className="relative z-10">
            <div className="flex items-start justify-between mb-4">
                <div className="w-11 h-11 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
                    {icon}
                </div>
                {subValue !== undefined && (
                    <span className="text-[11px] font-bold px-2.5 py-1 rounded-full bg-white/20 backdrop-blur-sm">
            {subLabel}: {subValue}
          </span>
                )}
            </div>
            <div className="text-3xl font-extrabold leading-none mb-1">{value}</div>
            <div className="text-xs font-bold uppercase tracking-widest opacity-80">{label}</div>
        </div>
    </div>
);

// ============================================================
// KPI KARTI (Nötr)
// ============================================================
const KpiCardLight = ({ label, value, icon, iconBg, extra }) => (
    <div className="bg-white dark:bg-navy-800 rounded-3xl border border-gray-100 dark:border-navy-700 p-5 shadow-sm hover:shadow-md transition-all">
        <div className={`w-11 h-11 rounded-2xl flex items-center justify-center mb-4 ${iconBg}`}>
            {icon}
        </div>
        <div className="text-2xl font-extrabold text-navy-700 dark:text-white mb-0.5">{value}</div>
        <div className="text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest">{label}</div>
        {extra && <div className="mt-2 text-[11px] font-bold text-gray-400 dark:text-gray-500">{extra}</div>}
    </div>
);

// ============================================================
// KART WRAPPER
// ============================================================
const Card = ({ title, subtitle, icon, iconBg, children, headerExtra, className = "" }) => (
    <div className={`bg-white dark:bg-navy-800 rounded-3xl border border-gray-100 dark:border-navy-700 shadow-sm overflow-hidden ${className}`}>
        <div className="px-5 py-4 border-b border-gray-100 dark:border-navy-700 flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3">
                {icon && (
                    <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${iconBg || "bg-brand-100 dark:bg-brand-900/20"}`}>
                        {icon}
                    </div>
                )}
                <div>
                    <h3 className="font-bold text-navy-700 dark:text-white text-base">{title}</h3>
                    {subtitle && <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{subtitle}</p>}
                </div>
            </div>
            {headerExtra}
        </div>
        {children}
    </div>
);

// ============================================================
// MODERN SELECT
// ============================================================
const MSelect = ({ value, onChange, options, disabled }) => (
    <div className="relative">
        <select
            value={value}
            onChange={onChange}
            disabled={disabled}
            className="w-full appearance-none bg-white dark:bg-navy-900 border border-gray-200 dark:border-navy-600 rounded-xl px-4 pr-9 py-2.5 text-sm font-semibold text-navy-700 dark:text-white outline-none focus:border-brand-500 transition-all cursor-pointer disabled:opacity-50"
        >
            {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400 text-[10px]">▾</div>
    </div>
);

// ============================================================
// YILDIZ GÖSTERGE
// ============================================================
const StarRating = ({ value }) => {
    const stars = Math.round(Number(value));
    return (
        <div className="flex items-center gap-0.5">
            {Array.from({ length: 5 }).map((_, i) => (
                <FiStar
                    key={i}
                    size={12}
                    className={i < stars ? "text-amber-400 fill-amber-400" : "text-gray-200 dark:text-navy-700"}
                />
            ))}
            <span className="ml-1.5 text-xs font-bold text-amber-500">{value > 0 ? value : "N/A"}</span>
        </div>
    );
};

// ============================================================
// DURUM BADGE
// ============================================================
const StatusBadge = ({ status }) => {
    const cfg = STATUS_CFG[status] || { label: status, color: "#94a3b8", bg: "rgba(148,163,184,0.12)" };
    return (
        <span
            className="text-[10px] font-bold px-2.5 py-1 rounded-full"
            style={{ color: cfg.color, backgroundColor: cfg.bg }}
        >
      {cfg.label}
    </span>
    );
};

// ============================================================
// PAGINATION
// ============================================================
const Pagination = ({ page, total, pageSize, onChange }) => {
    const totalPages = Math.ceil(total / pageSize);
    if (totalPages <= 1) return null;
    const pages = Array.from({ length: totalPages }, (_, i) => i + 1)
        .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
        .reduce((acc, p, idx, arr) => {
            if (idx > 0 && p - arr[idx - 1] > 1) acc.push(-1);
            acc.push(p);
            return acc;
        }, []);

    return (
        <div className="flex items-center justify-between px-5 py-4 border-t border-gray-100 dark:border-navy-700">
      <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">
        <span className="font-bold text-navy-700 dark:text-white">{(page - 1) * pageSize + 1}–{Math.min(page * pageSize, total)}</span> / {total}
      </span>
            <div className="flex items-center gap-1">
                <button onClick={() => onChange(page - 1)} disabled={page === 1}
                        className="w-8 h-8 rounded-xl text-gray-400 hover:bg-gray-100 dark:hover:bg-navy-700 transition-all disabled:opacity-40 flex items-center justify-center">
                    ‹
                </button>
                {pages.map((p, i) => p === -1 ? (
                    <span key={i} className="w-8 text-center text-gray-400">…</span>
                ) : (
                    <button key={p} onClick={() => onChange(p)}
                            className={`w-8 h-8 rounded-xl text-sm font-bold transition-all ${p === page ? "bg-brand-500 text-white shadow-md shadow-brand-500/30" : "text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-navy-700"}`}>
                        {p}
                    </button>
                ))}
                <button onClick={() => onChange(page + 1)} disabled={page === totalPages}
                        className="w-8 h-8 rounded-xl text-gray-400 hover:bg-gray-100 dark:hover:bg-navy-700 transition-all disabled:opacity-40 flex items-center justify-center">
                    ›
                </button>
            </div>
        </div>
    );
};

// ============================================================
// ANA BILEŞEN
// ============================================================
const Admin_Staff_Analytics = () => {
    const isDark = document.documentElement.classList.contains("dark");
    const chartTextColor = isDark ? "#94a3b8" : "#64748b";
    const chartGridColor = isDark ? "rgba(148,163,184,0.08)" : "rgba(148,163,184,0.15)";

    // STATE
    const [appointments, setAppointments] = useState([]);
    const [services, setServices]         = useState([]);
    const [staff, setStaff]               = useState([]);
    const [ratings, setRatings]           = useState([]);
    const [loading, setLoading]           = useState(true);
    const [showFilters, setShowFilters]   = useState(true);
    const [viewMode, setViewMode]         = useState("monthly"); // "monthly" | "daily"

    const now = new Date();
    const [filters, setFilters] = useState({
        startDate: new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split("T")[0],
        endDate: now.toISOString().split("T")[0],
        selectedMonth: now.getMonth() + 1,
        selectedYear: now.getFullYear(),
        selectedStaff: "",
        selectedService: "",
        selectedStatus: "",
    });

    // Table pages
    const [cancelledPage, setCancelledPage]       = useState(1);
    const [topStaffPage, setTopStaffPage]         = useState(1);
    const [topServicesPage, setTopServicesPage]   = useState(1);
    const [topRatedPage, setTopRatedPage]         = useState(1);
    const PAGE_SIZE = 5;

    // ============================================================
    // VERİ ÇEKME
    // ============================================================
    const fetchAllData = useCallback(async () => {
        setLoading(true);
        try {
            const [apptRes, srvRes, stfRes, ratingRes] = await Promise.all([
                appointmentsAPI.getAll(),
                servicesAPI.getAll(),
                usersAPI.getStaff(),
                ratingsAPI.getAll(),
            ]);
            setAppointments(apptRes.appointments || apptRes.data?.appointments || []);
            setServices(srvRes.services || srvRes.data?.services || []);
            setStaff(stfRes.staff || stfRes.data?.staff || []);
            setRatings(ratingRes.ratings || ratingRes.data?.ratings || []);
        } catch (err) {
            console.error("Veri yükleme hatası:", err);
            toast.error("Veriler yüklenirken bir hata oluştu.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchAllData(); }, [fetchAllData]);

    // ============================================================
    // FİLTRELENMİŞ RANDEVULAR
    // ============================================================
    const filteredAppointments = useMemo(() => {
        return appointments.filter(apt => {
            const d = new Date(apt.appointment_date);
            if (d < new Date(filters.startDate) || d > new Date(filters.endDate)) return false;
            if (filters.selectedStaff && apt.staff_id !== parseInt(filters.selectedStaff)) return false;
            if (filters.selectedService && apt.service_id !== parseInt(filters.selectedService)) return false;
            if (filters.selectedStatus && apt.status !== filters.selectedStatus) return false;
            return true;
        });
    }, [appointments, filters]);

    // ============================================================
    // KPI HESAPLAMALARI
    // ============================================================
    const kpiData = useMemo(() => {
        const total     = filteredAppointments.length;
        const completed = filteredAppointments.filter(a => a.status === "completed").length;
        const cancelled = filteredAppointments.filter(a => a.status === "cancelled").length;
        const pending   = filteredAppointments.filter(a => a.status === "pending").length;
        const approved  = filteredAppointments.filter(a => a.status === "approved").length;
        const no_show   = filteredAppointments.filter(a => a.status === "no_show").length;
        const cancelRate = total > 0 ? ((cancelled / total) * 100).toFixed(1) : 0;
        const completionRate = total > 0 ? ((completed / total) * 100).toFixed(1) : 0;

        const completedIds = filteredAppointments.filter(a => a.status === "completed").map(a => a.id);
        const relRatings = ratings.filter(r => completedIds.includes(r.appointment_id));
        const avgRating = relRatings.length > 0
            ? (relRatings.reduce((s, r) => s + r.rating, 0) / relRatings.length).toFixed(1)
            : 0;

        const serviceCounts = {};
        filteredAppointments.forEach(a => { serviceCounts[a.service_id] = (serviceCounts[a.service_id] || 0) + 1; });
        const popularId = Object.keys(serviceCounts).sort((a, b) => serviceCounts[b] - serviceCounts[a])[0];
        const mostPopularService = services.find(s => s.id === parseInt(popularId))?.name || "N/A";

        return { total, completed, cancelled, pending, approved, no_show, cancelRate, completionRate, avgRating, mostPopularService };
    }, [filteredAppointments, ratings, services]);

    // ============================================================
    // GRAFİK VERİLERİ
    // ============================================================

    // 1. Trend (Aylık / Günlük)
    const trendSeries = useMemo(() => {
        if (viewMode === "monthly") {
            const data = Array(12).fill(0);
            const completedData = Array(12).fill(0);
            filteredAppointments.forEach(apt => {
                const d = new Date(apt.appointment_date);
                if (d.getFullYear() === filters.selectedYear) {
                    const m = d.getMonth();
                    data[m]++;
                    if (apt.status === "completed") completedData[m]++;
                }
            });
            return {
                categories: MONTH_NAMES,
                series: [
                    { name: "Toplam Randevu", data },
                    { name: "Tamamlanan",     data: completedData },
                ],
            };
        } else {
            const year  = filters.selectedYear;
            const month = filters.selectedMonth - 1;
            const days  = new Date(year, month + 1, 0).getDate();
            const data = Array(days).fill(0);
            const completedData = Array(days).fill(0);
            filteredAppointments.forEach(apt => {
                const d = new Date(apt.appointment_date);
                if (d.getFullYear() === year && d.getMonth() === month) {
                    data[d.getDate() - 1]++;
                    if (apt.status === "completed") completedData[d.getDate() - 1]++;
                }
            });
            return {
                categories: Array.from({ length: days }, (_, i) => `${i + 1}`),
                series: [
                    { name: "Toplam Randevu", data },
                    { name: "Tamamlanan",     data: completedData },
                ],
            };
        }
    }, [filteredAppointments, viewMode, filters.selectedYear, filters.selectedMonth]);

    // 2. Durum Dağılımı (Donut)
    const statusDonut = useMemo(() => ({
        series: [kpiData.completed, kpiData.cancelled, kpiData.pending, kpiData.approved, kpiData.no_show],
        labels: ["Tamamlandı","İptal","Bekleyen","Onaylandı","Gelmedi"],
        colors: ["#10b981","#ef4444","#f59e0b","#0ea5e9","#f97316"],
    }), [kpiData]);

    // 3. Hizmet Dağılımı (Horizontal Bar)
    const serviceBarData = useMemo(() => {
        const counts = {};
        filteredAppointments.forEach(apt => {
            const srv = services.find(s => s.id === apt.service_id);
            if (srv) counts[srv.name] = (counts[srv.name] || 0) + 1;
        });
        const sorted = Object.entries(counts).sort((a, b) => b[1] - a[1]).slice(0, 10);
        return { categories: sorted.map(([n]) => n), data: sorted.map(([,v]) => v) };
    }, [filteredAppointments, services]);

    // 4. Personel Performansı (Bar)
    const staffBarData = useMemo(() => {
        const counts = {};
        filteredAppointments.forEach(apt => {
            const s = staff.find(x => x.id === apt.staff_id);
            if (s) counts[s.name] = (counts[s.name] || 0) + 1;
        });
        const sorted = Object.entries(counts).sort((a, b) => b[1] - a[1]).slice(0, 10);
        return { categories: sorted.map(([n]) => n), data: sorted.map(([,v]) => v) };
    }, [filteredAppointments, staff]);

    // 5. Top Staff Tablosu
    const topStaffData = useMemo(() => {
        const statsMap = {};
        filteredAppointments.forEach(apt => {
            const s = staff.find(x => x.id === apt.staff_id);
            if (!s) return;
            if (!statsMap[apt.staff_id]) statsMap[apt.staff_id] = { id: apt.staff_id, name: s.name, surname: s.surname || "", total: 0, completed: 0, cancelled: 0, avgRating: 0, ratingCount: 0 };
            statsMap[apt.staff_id].total++;
            if (apt.status === "completed") statsMap[apt.staff_id].completed++;
            if (apt.status === "cancelled") statsMap[apt.staff_id].cancelled++;
        });
        ratings.forEach(r => {
            const apt = appointments.find(a => a.id === r.appointment_id);
            if (apt && statsMap[apt.staff_id]) {
                statsMap[apt.staff_id].avgRating += r.rating;
                statsMap[apt.staff_id].ratingCount++;
            }
        });
        Object.values(statsMap).forEach(s => {
            if (s.ratingCount > 0) s.avgRating = (s.avgRating / s.ratingCount).toFixed(1);
        });
        return Object.values(statsMap).sort((a, b) => b.total - a.total);
    }, [filteredAppointments, staff, ratings, appointments]);

    // 6. Top Hizmetler Tablosu
    const topServicesData = useMemo(() => {
        const counts = {};
        filteredAppointments.forEach(apt => {
            const srv = services.find(s => s.id === apt.service_id);
            if (srv) {
                if (!counts[apt.service_id]) counts[apt.service_id] = { name: srv.name, count: 0, revenue: 0 };
                counts[apt.service_id].count++;
                counts[apt.service_id].revenue += parseFloat(srv.price || 0);
            }
        });
        return Object.values(counts).sort((a, b) => b.count - a.count);
    }, [filteredAppointments, services]);

    // 7. En Yüksek Puanlı Personeller
    const topRatedStaffData = useMemo(() => {
        const statsMap = {};
        ratings.forEach(r => {
            const apt = appointments.find(a => a.id === r.appointment_id);
            if (!apt) return;
            const s = staff.find(x => x.id === apt.staff_id);
            if (!s) return;
            if (!statsMap[apt.staff_id]) statsMap[apt.staff_id] = { name: s.name, totalRating: 0, count: 0 };
            statsMap[apt.staff_id].totalRating += r.rating;
            statsMap[apt.staff_id].count++;
        });
        return Object.values(statsMap)
            .map(s => ({ name: s.name, avgRating: (s.totalRating / s.count).toFixed(1), ratingCount: s.count }))
            .sort((a, b) => b.avgRating - a.avgRating);
    }, [ratings, appointments, staff]);

    // 8. İptal Edilen Randevular
    const cancelledAppointments = useMemo(() => {
        return filteredAppointments
            .filter(a => a.status === "cancelled")
            .sort((a, b) => new Date(b.appointment_date) - new Date(a.appointment_date))
            .map(apt => ({
                ...apt,
                serviceName: services.find(s => s.id === apt.service_id)?.name || "N/A",
                staffName: (() => { const s = staff.find(x => x.id === apt.staff_id); return s ? `${s.name} ${s.surname || ""}`.trim() : "N/A"; })(),
            }));
    }, [filteredAppointments, services, staff]);

    // ============================================================
    // FİLTRE İŞLEMLERİ
    // ============================================================
    const handleFilterChange = (field, value) => {
        setFilters(prev => ({ ...prev, [field]: value }));
        setCancelledPage(1); setTopStaffPage(1); setTopServicesPage(1); setTopRatedPage(1);
    };

    const handleMonthChange = (month) => {
        const year = filters.selectedYear;
        const startDate = new Date(year, month - 1, 1).toISOString().split("T")[0];
        const endDate   = new Date(year, month, 0).toISOString().split("T")[0];
        setFilters(prev => ({ ...prev, selectedMonth: month, startDate, endDate }));
    };

    const resetFilters = () => {
        const today = new Date();
        setFilters({
            startDate: new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split("T")[0],
            endDate: today.toISOString().split("T")[0],
            selectedMonth: today.getMonth() + 1,
            selectedYear: today.getFullYear(),
            selectedStaff: "", selectedService: "", selectedStatus: "",
        });
        setCancelledPage(1); setTopStaffPage(1); setTopServicesPage(1); setTopRatedPage(1);
    };

    // ============================================================
    // APEXCHARTS OPSİYONLARI
    // ============================================================
    const trendOptions = useMemo(() => ({
        chart: { type: "area", toolbar: { show: false }, background: "transparent", zoom: { enabled: false } },
        colors: [CHART_PALETTE[0], CHART_PALETTE[1]],
        stroke: { curve: "smooth", width: 2.5 },
        fill: { type: "gradient", gradient: { shadeIntensity: 1, opacityFrom: 0.22, opacityTo: 0.02, stops: [0, 90, 100] } },
        grid: { borderColor: chartGridColor, strokeDashArray: 4, xaxis: { lines: { show: false } } },
        xaxis: {
            categories: trendSeries.categories,
            labels: { style: { colors: chartTextColor, fontSize: "11px", fontWeight: 600 }, rotate: -30 },
            axisBorder: { show: false }, axisTicks: { show: false },
        },
        yaxis: {
            labels: { style: { colors: chartTextColor, fontSize: "11px" },
                formatter: v => Number.isInteger(v) ? v : v.toFixed(0) },
        },
        tooltip: { theme: isDark ? "dark" : "light", y: { formatter: v => `${v} Randevu` } },
        legend: { position: "top", horizontalAlign: "right", labels: { colors: chartTextColor }, markers: { radius: 6 } },
        dataLabels: { enabled: false },
    }), [trendSeries.categories, chartTextColor, chartGridColor, isDark]);

    const donutOptions = useMemo(() => ({
        chart: { type: "donut", background: "transparent" },
        colors: statusDonut.colors,
        labels: statusDonut.labels,
        legend: { position: "bottom", labels: { colors: chartTextColor }, markers: { radius: 6 } },
        dataLabels: { enabled: true, formatter: val => `${val.toFixed(1)}%`, style: { fontSize: "11px", fontWeight: 700 }, dropShadow: { enabled: false } },
        plotOptions: {
            pie: { donut: {
                    size: "72%",
                    labels: {
                        show: true,
                        total: { show: true, label: "Toplam", color: chartTextColor, fontSize: "13px", fontWeight: 700, formatter: () => kpiData.total },
                        value: { color: chartTextColor, fontSize: "14px", fontWeight: 700 },
                    },
                }},
        },
        tooltip: { theme: isDark ? "dark" : "light", y: { formatter: v => `${v} Randevu` } },
        stroke: { width: 0 },
    }), [statusDonut, chartTextColor, isDark, kpiData.total]);

    const serviceBarOptions = useMemo(() => ({
        chart: { type: "bar", toolbar: { show: false }, background: "transparent" },
        colors: [CHART_PALETTE[2]],
        plotOptions: { bar: { horizontal: true, barHeight: "62%", borderRadius: 6, borderRadiusApplication: "end", distributed: false } },
        grid: { borderColor: chartGridColor, strokeDashArray: 4, yaxis: { lines: { show: false } } },
        xaxis: {
            labels: { style: { colors: chartTextColor, fontSize: "11px" }, formatter: v => `${v}` },
            axisBorder: { show: false }, axisTicks: { show: false },
        },
        yaxis: { labels: { style: { colors: chartTextColor, fontSize: "11px", fontWeight: 600 }, maxWidth: 140 } },
        tooltip: { theme: isDark ? "dark" : "light", y: { formatter: v => `${v} Randevu` } },
        dataLabels: {
            enabled: true,
            formatter: v => `${v}`,
            style: { fontSize: "11px", fontWeight: 700, colors: ["#fff"] },
            dropShadow: { enabled: false },
        },
        legend: { show: false },
    }), [chartTextColor, chartGridColor, isDark]);

    const staffBarOptions = useMemo(() => ({
        chart: { type: "bar", toolbar: { show: false }, background: "transparent" },
        colors: CHART_PALETTE,
        plotOptions: { bar: { columnWidth: "55%", borderRadius: 6, borderRadiusApplication: "end", distributed: true } },
        grid: { borderColor: chartGridColor, strokeDashArray: 4, xaxis: { lines: { show: false } } },
        xaxis: {
            categories: staffBarData.categories,
            labels: {
                style: { colors: chartTextColor, fontSize: "10px", fontWeight: 600 },
                rotate: -35, trim: true, maxHeight: 80,
            },
            axisBorder: { show: false }, axisTicks: { show: false },
        },
        yaxis: { labels: { style: { colors: chartTextColor, fontSize: "11px" }, formatter: v => `${v}` } },
        tooltip: { theme: isDark ? "dark" : "light", y: { formatter: v => `${v} Randevu` } },
        dataLabels: { enabled: false },
        legend: { show: false },
    }), [staffBarData.categories, chartTextColor, chartGridColor, isDark]);

    // ============================================================
    // LOADING STATE
    // ============================================================
    if (loading) {
        return (
            <div className="p-4 md:p-6 space-y-6">
                <div className="h-10 w-72 bg-gray-200 dark:bg-navy-700 rounded-2xl animate-pulse"></div>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    {Array.from({ length: 4 }).map((_, i) => <KpiSkeleton key={i} />)}
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                    <ChartSkeleton height={320} />
                    <ChartSkeleton height={320} />
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                    <TableSkeleton />
                    <TableSkeleton />
                </div>
            </div>
        );
    }

    // ============================================================
    // ANA RENDER
    // ============================================================
    return (
        <div className="p-4 md:p-6 space-y-6">
            <ToastContainer position="bottom-right" theme="colored" toastClassName="!rounded-2xl !font-bold" />
            <style>{`
        @keyframes fadeInUp { from { opacity:0; transform:translateY(12px); } to { opacity:1; transform:translateY(0); } }
        .sa-fade { animation: fadeInUp 0.35s ease-out both; }
      `}</style>

            {/* ====== BAŞLIK ====== */}
            <div className="sa-fade flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-brand-400 to-purple-500 flex items-center justify-center shadow-lg shadow-brand-500/30">
                        <FiBarChart2 className="text-white" size={22} />
                    </div>
                    <div>
                        <h1 className="text-2xl md:text-3xl font-bold text-navy-700 dark:text-white">Randevu Analizleri</h1>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                            Detaylı randevu ve personel performans analizi • {filteredAppointments.length} randevu
                        </p>
                    </div>
                </div>
                <button
                    onClick={fetchAllData}
                    disabled={loading}
                    className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold text-gray-500 dark:text-gray-400 border border-gray-200 dark:border-navy-600 bg-white dark:bg-navy-800 hover:bg-gray-50 dark:hover:bg-navy-700 transition-all shadow-sm"
                >
                    <FiRefreshCw size={14} className={loading ? "animate-spin" : ""} /> Yenile
                </button>
            </div>

            {/* ====== FİLTRE KARTI ====== */}
            <div className="sa-fade bg-white dark:bg-navy-800 rounded-3xl border border-gray-100 dark:border-navy-700 shadow-sm overflow-hidden">
                <button
                    onClick={() => setShowFilters(p => !p)}
                    className="w-full px-5 py-4 flex items-center justify-between hover:bg-gray-50/50 dark:hover:bg-navy-700/30 transition-all"
                >
                    <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-xl bg-brand-100 dark:bg-brand-900/20 flex items-center justify-center">
                            <FiFilter className="text-brand-500" size={16} />
                        </div>
                        <div className="text-left">
                            <h3 className="font-bold text-navy-700 dark:text-white text-base">Filtreler</h3>
                            <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                                {filters.startDate} → {filters.endDate}
                                {filters.selectedStaff ? ` • ${staff.find(s => String(s.id) === filters.selectedStaff)?.name || ""}` : ""}
                            </p>
                        </div>
                    </div>
                    {showFilters ? <FiChevronUp className="text-gray-400" size={18} /> : <FiChevronDown className="text-gray-400" size={18} />}
                </button>

                {showFilters && (
                    <div className="px-5 pb-5 border-t border-gray-100 dark:border-navy-700 pt-4 space-y-4"
                         style={{ animation: "fadeInUp 0.2s ease-out" }}>

                        {/* Satır 1 */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                            <div>
                                <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wide mb-1.5">Başlangıç</label>
                                <input type="date" value={filters.startDate}
                                       onChange={e => handleFilterChange("startDate", e.target.value)}
                                       className="w-full bg-white dark:bg-navy-900 border border-gray-200 dark:border-navy-600 rounded-xl px-4 py-2.5 text-sm font-semibold text-navy-700 dark:text-white outline-none focus:border-brand-500 transition-all" />
                            </div>
                            <div>
                                <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wide mb-1.5">Bitiş</label>
                                <input type="date" value={filters.endDate}
                                       onChange={e => handleFilterChange("endDate", e.target.value)}
                                       className="w-full bg-white dark:bg-navy-900 border border-gray-200 dark:border-navy-600 rounded-xl px-4 py-2.5 text-sm font-semibold text-navy-700 dark:text-white outline-none focus:border-brand-500 transition-all" />
                            </div>
                            <div>
                                <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wide mb-1.5">Ay</label>
                                <MSelect
                                    value={filters.selectedMonth}
                                    onChange={e => handleMonthChange(parseInt(e.target.value))}
                                    options={MONTH_NAMES.map((n, i) => ({ value: i + 1, label: n }))}
                                />
                            </div>
                            <div>
                                <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wide mb-1.5">Yıl</label>
                                <MSelect
                                    value={filters.selectedYear}
                                    onChange={e => handleFilterChange("selectedYear", parseInt(e.target.value))}
                                    options={Array.from({ length: 5 }, (_, i) => now.getFullYear() - i).map(y => ({ value: y, label: y }))}
                                />
                            </div>
                        </div>

                        {/* Satır 2 */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                            <div>
                                <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wide mb-1.5">Personel</label>
                                <MSelect
                                    value={filters.selectedStaff}
                                    onChange={e => handleFilterChange("selectedStaff", e.target.value)}
                                    options={[
                                        { value: "", label: "Tüm Personeller" },
                                        ...staff.map(s => ({ value: s.id, label: `${s.name} ${s.surname || ""}`.trim() })),
                                    ]}
                                />
                            </div>
                            <div>
                                <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wide mb-1.5">Hizmet</label>
                                <MSelect
                                    value={filters.selectedService}
                                    onChange={e => handleFilterChange("selectedService", e.target.value)}
                                    options={[
                                        { value: "", label: "Tüm Hizmetler" },
                                        ...services.map(s => ({ value: s.id, label: s.name })),
                                    ]}
                                />
                            </div>
                            <div>
                                <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wide mb-1.5">Durum</label>
                                <MSelect
                                    value={filters.selectedStatus}
                                    onChange={e => handleFilterChange("selectedStatus", e.target.value)}
                                    options={[
                                        { value: "", label: "Tüm Durumlar" },
                                        { value: "completed", label: "Tamamlandı" },
                                        { value: "pending",   label: "Bekleyen" },
                                        { value: "approved",  label: "Onaylandı" },
                                        { value: "cancelled", label: "İptal" },
                                        { value: "no_show",   label: "Gelmedi" },
                                    ]}
                                />
                            </div>
                            <div className="flex items-end">
                                <button
                                    onClick={resetFilters}
                                    className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-bold text-red-500 border border-red-200 dark:border-red-800/40 hover:bg-red-50 dark:hover:bg-red-900/10 transition-all"
                                >
                                    <FiX size={14} /> Filtreleri Temizle
                                </button>
                            </div>
                        </div>

                        {/* Görünüm Toggle */}
                        <div className="flex items-center gap-2">
                            <span className="text-xs font-bold text-gray-400 uppercase tracking-wide">Trend Görünümü:</span>
                            <div className="flex bg-gray-100 dark:bg-navy-900 rounded-xl p-1 gap-1">
                                {[{ v: "monthly", l: "Aylık" }, { v: "daily", l: "Günlük" }].map(opt => (
                                    <button key={opt.v} onClick={() => setViewMode(opt.v)}
                                            className={`px-4 py-1.5 rounded-lg text-xs font-bold transition-all
                      ${viewMode === opt.v ? "bg-white dark:bg-navy-800 text-navy-700 dark:text-white shadow-sm" : "text-gray-500 dark:text-gray-400 hover:text-navy-700 dark:hover:text-white"}`}>
                                        {opt.l}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* ====== KPI KARTLARI - SATIR 1 ====== */}
            <div className="sa-fade grid grid-cols-2 lg:grid-cols-4 gap-4">
                <KpiCard
                    label="Toplam Randevu"
                    value={kpiData.total}
                    icon={<FiCalendar size={20} className="text-white" />}
                    gradient="linear-gradient(135deg, #4318FF 0%, #7c3aed 100%)"
                />
                <KpiCard
                    label="Tamamlanan"
                    value={kpiData.completed}
                    subValue={`%${kpiData.completionRate}`}
                    subLabel="Oran"
                    icon={<FiCheckCircle size={20} className="text-white" />}
                    gradient="linear-gradient(135deg, #10b981 0%, #059669 100%)"
                />
                <KpiCard
                    label="İptal Edilen"
                    value={kpiData.cancelled}
                    subValue={`%${kpiData.cancelRate}`}
                    subLabel="Oran"
                    icon={<FiXCircle size={20} className="text-white" />}
                    gradient="linear-gradient(135deg, #ef4444 0%, #dc2626 100%)"
                />
                <KpiCard
                    label="Bekleyen / Onaylı"
                    value={kpiData.pending + kpiData.approved}
                    icon={<FiClock size={20} className="text-white" />}
                    gradient="linear-gradient(135deg, #f59e0b 0%, #d97706 100%)"
                />
            </div>

            {/* ====== KPI KARTLARI - SATIR 2 ====== */}
            <div className="sa-fade grid grid-cols-1 sm:grid-cols-3 gap-4">
                <KpiCardLight
                    label="Ortalama Puan"
                    value={kpiData.avgRating > 0 ? kpiData.avgRating : "—"}
                    icon={<FiStar size={18} className="text-amber-500 fill-amber-500" />}
                    iconBg="bg-amber-100 dark:bg-amber-900/20"
                    extra="Tamamlanan randevular üzerinden"
                />
                <KpiCardLight
                    label="Gelmedi"
                    value={kpiData.no_show}
                    icon={<FiAlertCircle size={18} className="text-orange-500" />}
                    iconBg="bg-orange-100 dark:bg-orange-900/20"
                    extra={`Toplam %${kpiData.total > 0 ? ((kpiData.no_show / kpiData.total) * 100).toFixed(1) : 0} oranında`}
                />
                <KpiCardLight
                    label="En Popüler Hizmet"
                    value={kpiData.mostPopularService}
                    icon={<FiScissors size={18} className="text-brand-500" />}
                    iconBg="bg-brand-100 dark:bg-brand-900/20"
                    extra="Randevu sayısına göre"
                />
            </div>

            {/* ====== TREND + DONUT ====== */}
            <div className="sa-fade grid grid-cols-1 lg:grid-cols-3 gap-5">
                <Card
                    title={viewMode === "monthly" ? `${filters.selectedYear} Yılı Aylık Trend` : `${MONTH_NAMES[filters.selectedMonth - 1]} ${filters.selectedYear} Günlük Trend`}
                    subtitle={`${trendSeries.series[0].data.reduce((a, b) => a + b, 0)} toplam randevu`}
                    icon={<FiActivity size={16} className="text-brand-500" />}
                    iconBg="bg-brand-100 dark:bg-brand-900/20"
                    className="lg:col-span-2"
                >
                    <div className="p-4">
                        {trendSeries.series[0].data.some(v => v > 0) ? (
                            <ReactApexChart type="area" series={trendSeries.series} options={trendOptions} height={300} />
                        ) : (
                            <div className="flex flex-col items-center justify-center py-14 text-center">
                                <FiBarChart2 size={32} className="text-gray-300 dark:text-gray-600 mb-3" />
                                <p className="text-gray-500 dark:text-gray-400 font-medium text-sm">Seçili dönemde veri yok.</p>
                            </div>
                        )}
                    </div>
                </Card>

                <Card
                    title="Durum Dağılımı"
                    subtitle="Filtreli randevu durumları"
                    icon={<FiPieChart size={16} className="text-emerald-500" />}
                    iconBg="bg-emerald-100 dark:bg-emerald-900/20"
                >
                    <div className="p-4">
                        <ReactApexChart type="donut" series={statusDonut.series} options={donutOptions} height={300} />
                    </div>
                </Card>
            </div>

            {/* ====== HİZMET + PERSONEL BAR CHART ====== */}
            <div className="sa-fade grid grid-cols-1 lg:grid-cols-2 gap-5">
                <Card
                    title="Hizmet Başına Randevu (Top 10)"
                    subtitle="En sık tercih edilen hizmetler"
                    icon={<FiScissors size={16} className="text-amber-500" />}
                    iconBg="bg-amber-100 dark:bg-amber-900/20"
                >
                    <div className="p-4">
                        {serviceBarData.categories.length > 0 ? (
                            <ReactApexChart
                                type="bar"
                                series={[{ name: "Randevu", data: serviceBarData.data }]}
                                options={serviceBarOptions}
                                height={Math.max(250, serviceBarData.categories.length * 40)}
                            />
                        ) : (
                            <div className="flex flex-col items-center justify-center py-14 text-center">
                                <FiScissors size={28} className="text-gray-300 dark:text-gray-600 mb-2" />
                                <p className="text-gray-500 dark:text-gray-400 text-sm font-medium">Veri bulunamadı.</p>
                            </div>
                        )}
                    </div>
                </Card>

                <Card
                    title="Personel Randevu Sayısı (Top 10)"
                    subtitle="En çok randevu alan personeller"
                    icon={<FiUser size={16} className="text-blue-500" />}
                    iconBg="bg-blue-100 dark:bg-blue-900/20"
                >
                    <div className="p-4">
                        {staffBarData.categories.length > 0 ? (
                            <ReactApexChart
                                type="bar"
                                series={[{ name: "Randevu", data: staffBarData.data }]}
                                options={staffBarOptions}
                                height={300}
                            />
                        ) : (
                            <div className="flex flex-col items-center justify-center py-14 text-center">
                                <FiUser size={28} className="text-gray-300 dark:text-gray-600 mb-2" />
                                <p className="text-gray-500 dark:text-gray-400 text-sm font-medium">Veri bulunamadı.</p>
                            </div>
                        )}
                    </div>
                </Card>
            </div>

            {/* ====== TOP PERSONEL TABLOSU ====== */}
            <div className="sa-fade">
                <Card
                    title="Personel Performans Detayı"
                    subtitle={`${topStaffData.length} personel`}
                    icon={<FiAward size={16} className="text-brand-500" />}
                    iconBg="bg-brand-100 dark:bg-brand-900/20"
                >
                    {topStaffData.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-12 text-center">
                            <FiUser size={28} className="text-gray-300 dark:text-gray-600 mb-2" />
                            <p className="text-gray-500 dark:text-gray-400 text-sm font-medium">Veri bulunamadı.</p>
                        </div>
                    ) : (
                        <>
                            {/* Masaüstü tablo */}
                            <div className="hidden md:block overflow-x-auto">
                                <table className="w-full">
                                    <thead>
                                    <tr className="bg-gray-50 dark:bg-navy-900/50">
                                        {["Personel", "Toplam", "Tamamlanan", "İptal", "Ort. Puan"].map(h => (
                                            <th key={h} className="px-5 py-3 text-left text-[11px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider">{h}</th>
                                        ))}
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {topStaffData.slice((topStaffPage - 1) * PAGE_SIZE, topStaffPage * PAGE_SIZE).map(s => (
                                        <tr key={s.id} className="border-b border-gray-100 dark:border-navy-700 hover:bg-gray-50/50 dark:hover:bg-navy-700/30 transition-colors">
                                            <td className="px-5 py-3.5">
                                                <div className="flex items-center gap-2">
                                                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-brand-400 to-purple-500 flex items-center justify-center text-white font-bold text-xs shrink-0">
                                                        {getInitials(s.name, s.surname)}
                                                    </div>
                                                    <span className="font-bold text-sm text-navy-700 dark:text-white">{s.name} {s.surname}</span>
                                                </div>
                                            </td>
                                            <td className="px-5 py-3.5">
                                                <span className="text-sm font-bold text-navy-700 dark:text-white">{s.total}</span>
                                            </td>
                                            <td className="px-5 py-3.5">
                          <span className="inline-flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-full bg-emerald-100 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400">
                            <FiCheckCircle size={10} /> {s.completed}
                          </span>
                                            </td>
                                            <td className="px-5 py-3.5">
                          <span className="inline-flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-full bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-400">
                            <FiXCircle size={10} /> {s.cancelled}
                          </span>
                                            </td>
                                            <td className="px-5 py-3.5">
                                                <StarRating value={s.avgRating} />
                                            </td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* Mobil kartlar */}
                            <div className="md:hidden p-4 space-y-3">
                                {topStaffData.slice((topStaffPage - 1) * PAGE_SIZE, topStaffPage * PAGE_SIZE).map(s => (
                                    <div key={s.id} className="p-4 rounded-2xl border border-gray-100 dark:border-navy-700 bg-gray-50/50 dark:bg-navy-900/50">
                                        <div className="flex items-center gap-3 mb-3">
                                            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-brand-400 to-purple-500 flex items-center justify-center text-white font-bold text-sm shrink-0">
                                                {getInitials(s.name, s.surname)}
                                            </div>
                                            <div>
                                                <div className="font-bold text-navy-700 dark:text-white text-sm">{s.name} {s.surname}</div>
                                                <StarRating value={s.avgRating} />
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-3 gap-2">
                                            {[
                                                { label: "Toplam", val: s.total, color: "text-navy-700 dark:text-white" },
                                                { label: "Tamamlandı", val: s.completed, color: "text-emerald-600 dark:text-emerald-400" },
                                                { label: "İptal", val: s.cancelled, color: "text-red-600 dark:text-red-400" },
                                            ].map(item => (
                                                <div key={item.label} className="text-center p-2 rounded-xl bg-white dark:bg-navy-800">
                                                    <div className={`text-lg font-extrabold ${item.color}`}>{item.val}</div>
                                                    <div className="text-[10px] font-bold text-gray-400 dark:text-gray-500">{item.label}</div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <Pagination page={topStaffPage} total={topStaffData.length} pageSize={PAGE_SIZE} onChange={setTopStaffPage} />
                        </>
                    )}
                </Card>
            </div>

            {/* ====== TOP HİZMETLER + EN YÜKSEK PUANLI ====== */}
            <div className="sa-fade grid grid-cols-1 lg:grid-cols-2 gap-5">
                {/* Top Hizmetler */}
                <Card
                    title="En Çok Tercih Edilen Hizmetler"
                    subtitle={`${topServicesData.length} hizmet`}
                    icon={<FiScissors size={16} className="text-amber-500" />}
                    iconBg="bg-amber-100 dark:bg-amber-900/20"
                >
                    {topServicesData.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-12 text-center">
                            <FiScissors size={28} className="text-gray-300 dark:text-gray-600 mb-2" />
                            <p className="text-gray-500 dark:text-gray-400 text-sm font-medium">Veri bulunamadı.</p>
                        </div>
                    ) : (
                        <>
                            {/* Masaüstü tablo */}
                            <div className="hidden md:block overflow-x-auto">
                                <table className="w-full">
                                    <thead>
                                    <tr className="bg-gray-50 dark:bg-navy-900/50">
                                        <th className="px-5 py-3 text-left text-[11px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Hizmet</th>
                                        <th className="px-5 py-3 text-left text-[11px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Randevu</th>
                                        <th className="px-5 py-3 text-left text-[11px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Tahmini Gelir</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {topServicesData.slice((topServicesPage - 1) * PAGE_SIZE, topServicesPage * PAGE_SIZE).map((s, i) => (
                                        <tr key={i} className="border-b border-gray-100 dark:border-navy-700 hover:bg-gray-50/50 dark:hover:bg-navy-700/30 transition-colors">
                                            <td className="px-5 py-3.5">
                                                <div className="flex items-center gap-2">
                                                    <div className="w-7 h-7 rounded-lg flex items-center justify-center text-[10px] font-extrabold text-white shrink-0"
                                                         style={{ backgroundColor: CHART_PALETTE[i % CHART_PALETTE.length] }}>
                                                        {i + 1}
                                                    </div>
                                                    <span className="font-bold text-sm text-navy-700 dark:text-white">{s.name}</span>
                                                </div>
                                            </td>
                                            <td className="px-5 py-3.5">
                          <span className="inline-flex items-center text-xs font-bold px-2.5 py-1 rounded-full bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400">
                            {s.count}
                          </span>
                                            </td>
                                            <td className="px-5 py-3.5">
                                                <span className="text-sm font-bold text-emerald-700 dark:text-emerald-400">{formatCurrency(s.revenue)}</span>
                                            </td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* Mobil kartlar */}
                            <div className="md:hidden p-4 space-y-3">
                                {topServicesData.slice((topServicesPage - 1) * PAGE_SIZE, topServicesPage * PAGE_SIZE).map((s, i) => (
                                    <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 dark:bg-navy-900 border border-gray-100 dark:border-navy-700">
                                        <div className="w-8 h-8 rounded-lg flex items-center justify-center text-[11px] font-extrabold text-white shrink-0"
                                             style={{ backgroundColor: CHART_PALETTE[i % CHART_PALETTE.length] }}>
                                            {i + 1}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="font-bold text-sm text-navy-700 dark:text-white truncate">{s.name}</div>
                                            <div className="text-xs text-emerald-600 dark:text-emerald-400 font-bold">{formatCurrency(s.revenue)}</div>
                                        </div>
                                        <span className="text-xs font-bold text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 px-2.5 py-1 rounded-full shrink-0">{s.count}</span>
                                    </div>
                                ))}
                            </div>

                            <Pagination page={topServicesPage} total={topServicesData.length} pageSize={PAGE_SIZE} onChange={setTopServicesPage} />
                        </>
                    )}
                </Card>

                {/* En Yüksek Puanlı Personeller */}
                <Card
                    title="En Yüksek Puanlı Personeller"
                    subtitle={`${topRatedStaffData.length} personel`}
                    icon={<FiStar size={16} className="text-amber-500" />}
                    iconBg="bg-amber-100 dark:bg-amber-900/20"
                >
                    {topRatedStaffData.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-12 text-center">
                            <FiStar size={28} className="text-gray-300 dark:text-gray-600 mb-2" />
                            <p className="text-gray-500 dark:text-gray-400 text-sm font-medium">Henüz değerlendirme yok.</p>
                        </div>
                    ) : (
                        <>
                            {/* Masaüstü tablo */}
                            <div className="hidden md:block overflow-x-auto">
                                <table className="w-full">
                                    <thead>
                                    <tr className="bg-gray-50 dark:bg-navy-900/50">
                                        <th className="px-5 py-3 text-left text-[11px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Personel</th>
                                        <th className="px-5 py-3 text-left text-[11px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Ort. Puan</th>
                                        <th className="px-5 py-3 text-left text-[11px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Değerlendirme</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {topRatedStaffData.slice((topRatedPage - 1) * PAGE_SIZE, topRatedPage * PAGE_SIZE).map((s, i) => (
                                        <tr key={i} className="border-b border-gray-100 dark:border-navy-700 hover:bg-gray-50/50 dark:hover:bg-navy-700/30 transition-colors">
                                            <td className="px-5 py-3.5">
                                                <div className="flex items-center gap-2">
                                                    {i < 3 && (
                                                        <span className="text-sm">{["🥇","🥈","🥉"][i]}</span>
                                                    )}
                                                    <span className="font-bold text-sm text-navy-700 dark:text-white">{s.name}</span>
                                                </div>
                                            </td>
                                            <td className="px-5 py-3.5"><StarRating value={s.avgRating} /></td>
                                            <td className="px-5 py-3.5">
                          <span className="inline-flex items-center text-xs font-bold px-2.5 py-1 rounded-full bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400">
                            {s.ratingCount} yorum
                          </span>
                                            </td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* Mobil kartlar */}
                            <div className="md:hidden p-4 space-y-3">
                                {topRatedStaffData.slice((topRatedPage - 1) * PAGE_SIZE, topRatedPage * PAGE_SIZE).map((s, i) => (
                                    <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 dark:bg-navy-900 border border-gray-100 dark:border-navy-700">
                                        {i < 3 && <span className="text-xl shrink-0">{["🥇","🥈","🥉"][i]}</span>}
                                        <div className="flex-1 min-w-0">
                                            <div className="font-bold text-sm text-navy-700 dark:text-white">{s.name}</div>
                                            <StarRating value={s.avgRating} />
                                        </div>
                                        <span className="text-xs font-bold text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 px-2.5 py-1 rounded-full shrink-0">{s.ratingCount}</span>
                                    </div>
                                ))}
                            </div>

                            <Pagination page={topRatedPage} total={topRatedStaffData.length} pageSize={PAGE_SIZE} onChange={setTopRatedPage} />
                        </>
                    )}
                </Card>
            </div>

            {/* ====== İPTAL EDİLEN RANDEVULAR ====== */}
            <div className="sa-fade">
                <Card
                    title="Son İptal Edilen Randevular"
                    subtitle={`${cancelledAppointments.length} iptal`}
                    icon={<FiXCircle size={16} className="text-red-500" />}
                    iconBg="bg-red-100 dark:bg-red-900/20"
                >
                    {cancelledAppointments.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-12 text-center">
                            <FiCheckCircle size={28} className="text-emerald-400 mb-2" />
                            <p className="text-gray-500 dark:text-gray-400 text-sm font-medium">Bu dönemde iptal bulunmuyor.</p>
                        </div>
                    ) : (
                        <>
                            {/* Masaüstü tablo */}
                            <div className="hidden md:block overflow-x-auto">
                                <table className="w-full">
                                    <thead>
                                    <tr className="bg-gray-50 dark:bg-navy-900/50">
                                        {["Tarih", "Saat", "Hizmet", "Personel", "Durum"].map(h => (
                                            <th key={h} className="px-5 py-3 text-left text-[11px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider">{h}</th>
                                        ))}
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {cancelledAppointments.slice((cancelledPage - 1) * PAGE_SIZE, cancelledPage * PAGE_SIZE).map((apt, i) => (
                                        <tr key={i} className="border-b border-gray-100 dark:border-navy-700 hover:bg-gray-50/50 dark:hover:bg-navy-700/30 transition-colors">
                                            <td className="px-5 py-3.5">
                          <span className="text-sm font-bold text-navy-700 dark:text-white flex items-center gap-1">
                            <FiCalendar size={12} className="text-gray-400" />
                              {formatDate(apt.appointment_date)}
                          </span>
                                            </td>
                                            <td className="px-5 py-3.5">
                          <span className="text-sm text-gray-600 dark:text-gray-300">
                            {apt.appointment_time ? apt.appointment_time.substring(0, 5) : "—"}
                          </span>
                                            </td>
                                            <td className="px-5 py-3.5">
                                                <span className="text-sm text-navy-700 dark:text-white font-medium">{apt.serviceName}</span>
                                            </td>
                                            <td className="px-5 py-3.5">
                          <span className="text-sm text-navy-700 dark:text-white font-medium flex items-center gap-1">
                            <FiUser size={12} className="text-gray-400" /> {apt.staffName}
                          </span>
                                            </td>
                                            <td className="px-5 py-3.5">
                                                <StatusBadge status={apt.status} />
                                            </td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* Mobil kartlar */}
                            <div className="md:hidden p-4 space-y-3">
                                {cancelledAppointments.slice((cancelledPage - 1) * PAGE_SIZE, cancelledPage * PAGE_SIZE).map((apt, i) => (
                                    <div key={i} className="p-4 rounded-2xl border border-red-100 dark:border-red-900/30 bg-red-50/50 dark:bg-red-900/10">
                                        <div className="flex items-center justify-between mb-2">
                      <span className="font-bold text-sm text-navy-700 dark:text-white flex items-center gap-1">
                        <FiCalendar size={12} className="text-red-400" />
                          {formatDate(apt.appointment_date)}
                          {apt.appointment_time && <span className="text-gray-400 font-normal text-xs ml-1">{apt.appointment_time.substring(0, 5)}</span>}
                      </span>
                                            <StatusBadge status={apt.status} />
                                        </div>
                                        <div className="text-sm font-bold text-navy-700 dark:text-white mb-1">{apt.serviceName}</div>
                                        <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                                            <FiUser size={11} /> {apt.staffName}
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <Pagination page={cancelledPage} total={cancelledAppointments.length} pageSize={PAGE_SIZE} onChange={setCancelledPage} />
                        </>
                    )}
                </Card>
            </div>
        </div>
    );
};

export default Admin_Staff_Analytics;